/**
 * Service Area Editor Component
 *
 * Dialog-based editor for creating and editing service areas.
 * Uses tabbed interface for General info and Availability settings.
 * Follows LocationEditor pattern from locations feature.
 *
 * @example
 * <ServiceAreaEditor
 *   serviceArea={existingArea}
 *   isOpen={true}
 *   onClose={() => setOpen(false)}
 *   onSuccess={(area) => console.log('Saved!', area)}
 * />
 */

import React, { useState, useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Checkbox } from '@/components/ui/checkbox'
import { toast } from '@/components/ui/use-toast'
import { Save, MapPin, Calendar } from 'lucide-react'
import { ServiceAreaAvailabilityGrid } from './ServiceAreaAvailabilityGrid'
import {
  useCreateServiceArea,
  useUpdateServiceArea,
} from '../hooks/useServiceAreas'
import {
  serviceAreaSchema,
  type ServiceAreaFormValues,
} from '../utils/schedulerValidation'
import type { ServiceArea } from '../types'

interface ServiceAreaEditorProps {
  serviceArea?: ServiceArea
  isOpen: boolean
  onClose: () => void
  onSuccess?: (area: ServiceArea) => void
}

export function ServiceAreaEditor({
  serviceArea,
  isOpen,
  onClose,
  onSuccess,
}: ServiceAreaEditorProps) {
  const isEditing = !!serviceArea
  const [activeTab, setActiveTab] = useState('general')

  const createMutation = useCreateServiceArea()
  const updateMutation = useUpdateServiceArea()

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ServiceAreaFormValues>({
    resolver: zodResolver(serviceAreaSchema),
    defaultValues: serviceArea
      ? {
          name: serviceArea.name,
          franchise_region: serviceArea.franchise_region,
          display_order: serviceArea.display_order,
          is_active: serviceArea.is_active,
          monday: serviceArea.monday,
          tuesday: serviceArea.tuesday,
          wednesday: serviceArea.wednesday,
          thursday: serviceArea.thursday,
          friday: serviceArea.friday,
          saturday: serviceArea.saturday,
          sunday: serviceArea.sunday,
        }
      : {
          name: '',
          franchise_region: 'hamilton',
          display_order: 1,
          is_active: true,
          monday: true,
          tuesday: true,
          wednesday: true,
          thursday: true,
          friday: true,
          saturday: false,
          sunday: false,
        },
  })

  // Reset form when dialog opens/closes or serviceArea changes
  useEffect(() => {
    if (isOpen) {
      if (serviceArea) {
        reset({
          name: serviceArea.name,
          franchise_region: serviceArea.franchise_region,
          display_order: serviceArea.display_order,
          is_active: serviceArea.is_active,
          monday: serviceArea.monday,
          tuesday: serviceArea.tuesday,
          wednesday: serviceArea.wednesday,
          thursday: serviceArea.thursday,
          friday: serviceArea.friday,
          saturday: serviceArea.saturday,
          sunday: serviceArea.sunday,
        })
      } else {
        reset({
          name: '',
          franchise_region: 'hamilton',
          display_order: 1,
          is_active: true,
          monday: true,
          tuesday: true,
          wednesday: true,
          thursday: true,
          friday: true,
          saturday: false,
          sunday: false,
        })
      }
      setActiveTab('general')
    }
  }, [isOpen, serviceArea, reset])

  const onSubmit = async (data: ServiceAreaFormValues) => {
    try {
      if (isEditing && serviceArea) {
        const result = await updateMutation.mutateAsync({
          id: serviceArea.id,
          data,
        })

        toast({
          title: 'Service Area Updated',
          description: `${data.name} has been updated successfully.`,
        })

        if (onSuccess) onSuccess(result)
      } else {
        const result = await createMutation.mutateAsync(data)

        toast({
          title: 'Service Area Created',
          description: `${data.name} has been created successfully.`,
        })

        if (onSuccess) onSuccess(result)
      }

      onClose()
    } catch (error) {
      toast({
        title: 'Error',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to save service area. Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      onClose()
    }
  }

  // Watch day values for availability grid
  const daysValue = {
    monday: watch('monday'),
    tuesday: watch('tuesday'),
    wednesday: watch('wednesday'),
    thursday: watch('thursday'),
    friday: watch('friday'),
    saturday: watch('saturday'),
    sunday: watch('sunday'),
  }

  const handleDaysChange = (newDays: typeof daysValue) => {
    Object.entries(newDays).forEach(([key, value]) => {
      setValue(key as keyof ServiceAreaFormValues, value, {
        shouldValidate: true,
      })
    })
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Service Area' : 'Create Service Area'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update service area configuration and availability.'
              : 'Configure a new service area with available service days.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="general" className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                General
              </TabsTrigger>
              <TabsTrigger
                value="availability"
                className="flex items-center gap-2"
              >
                <Calendar className="h-4 w-4" />
                Availability
              </TabsTrigger>
            </TabsList>

            {/* General Tab */}
            <TabsContent value="general" className="space-y-4 mt-4">
              {/* Service Area Name */}
              <div className="space-y-2">
                <Label htmlFor="name">
                  Service Area Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  {...register('name')}
                  placeholder="e.g., Downtown Toronto, Hamilton West"
                  disabled={isSubmitting}
                />
                {errors.name && (
                  <p className="text-sm text-destructive">
                    {errors.name.message}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  A descriptive name for this service area (shown to
                  schedulers)
                </p>
              </div>

              {/* Franchise Region */}
              <div className="space-y-2">
                <Label htmlFor="franchise_region">
                  Franchise Region <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={watch('franchise_region')}
                  onValueChange={(value) =>
                    setValue('franchise_region', value as 'hamilton' | 'northyork', {
                      shouldValidate: true,
                    })
                  }
                  disabled={isSubmitting}
                >
                  <SelectTrigger id="franchise_region">
                    <SelectValue placeholder="Select franchise" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hamilton">Hamilton</SelectItem>
                    <SelectItem value="northyork">North York</SelectItem>
                  </SelectContent>
                </Select>
                {errors.franchise_region && (
                  <p className="text-sm text-destructive">
                    {errors.franchise_region.message}
                  </p>
                )}
              </div>

              {/* Display Order */}
              <div className="space-y-2">
                <Label htmlFor="display_order">Display Order</Label>
                <Input
                  id="display_order"
                  type="number"
                  min="1"
                  {...register('display_order', { valueAsNumber: true })}
                  disabled={isSubmitting}
                />
                {errors.display_order && (
                  <p className="text-sm text-destructive">
                    {errors.display_order.message}
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  Lower numbers appear first in selection lists
                </p>
              </div>

              {/* Active Status */}
              <div className="flex items-center space-x-2 p-4 border rounded-lg bg-muted/30">
                <Checkbox
                  id="is_active"
                  checked={watch('is_active')}
                  onCheckedChange={(checked) =>
                    setValue('is_active', !!checked, { shouldValidate: true })
                  }
                  disabled={isSubmitting}
                />
                <Label htmlFor="is_active" className="cursor-pointer">
                  <div className="font-medium">Active</div>
                  <div className="text-xs text-muted-foreground">
                    Only active service areas can be scheduled
                  </div>
                </Label>
              </div>
            </TabsContent>

            {/* Availability Tab */}
            <TabsContent value="availability" className="mt-4">
              <ServiceAreaAvailabilityGrid
                value={daysValue}
                onChange={handleDaysChange}
                error={errors.monday?.message}
                disabled={isSubmitting}
              />
            </TabsContent>
          </Tabs>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              <Save className="mr-2 h-4 w-4" />
              {isSubmitting
                ? 'Saving...'
                : isEditing
                  ? 'Update Service Area'
                  : 'Create Service Area'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
